import { getDeploymentInfo } from "@/utils/deployment";
import { cn } from "@/utils/ui";
import { useModal } from "@ebay/nice-modal-react";
import { useQuery } from "@tanstack/react-query";
import { Link, rootRouteId, useMatch, useRouter } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { H } from "highlight.run";
import {
  AlertTriangleIcon,
  BugIcon,
  HomeIcon,
  LoaderCircleIcon,
  RotateCcwIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { get } from "radashi";
import { useState } from "react";
import toast from "react-hot-toast";
import { P, match } from "ts-pattern";
import { Button } from "./ui/button";
import { StdModal, StdModalPresets } from "./ui/modal";

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  const router = useRouter();
  const isRoot = useMatch({
    strict: false,
    select: (state) => state.id === rootRouteId,
  });

  const statusCode =
    error instanceof Error ? get(error, "statusCode", 500) : 500;
  const errorMessage =
    error instanceof Error ? error.message : "An unexpected error occurred";

  const deploymentInfoQuery = useQuery({
    queryKey: ["deploymentInfo"],
    queryFn: getDeploymentInfo,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  const [errorReported, setErrorReported] = useState(false);

  // Generate a mock request ID
  const requestId = Math.random().toString(16).substring(2, 10).toUpperCase();

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
    toast.success("文本已复制到剪贴板");
  }

  const errorReportingModal = useModal(
    StdModal,
    StdModalPresets.alert({
      title: "真的要报告这一错误吗？",
      description:
        "遇到意料之外的错误了吗？点击⌈继续⌋，让我们一起解决这个问题。您的反馈将直接发送给开发团队，帮助我们不断改进产品体验。",
      headerDirection: "row",
      size: "lg",
      centered: false,
      actions: [
        {
          label: "还是算了",
          intent: "outline",
          flavor: "gray",
          act: "cancel",
        },
        {
          label: "我要狠狠地贡献口牙",
          intent: "solid",
          flavor: "destructive",
          act: "confirm",
          onClick: () => {
            H.consumeError(error);
            toast.success("错误已经成功提交，感谢您的反馈！");
            setErrorReported(true);
          },
        },
      ],
    }),
  );

  return (
    <motion.div
      className={cn(
        "flex items-center justify-center w-full p-4",
        isRoot && "min-h-screen",
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="relative w-full max-w-md p-6 border shadow-lg bg-destructivea-subtle-bg border-destructivea-border"
        initial={{ y: 20, scale: 0.95, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
          delay: 0.2,
        }}
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-destructivea-border to-destructivea-border-hover animate-pulse" />
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-destructivea-border-hover to-destructivea-border animate-pulse" />

        {/* Main error display */}
        <div className="relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <motion.div
              className="relative font-mono text-6xl font-bold text-red-500 animate-glitch"
              initial={{ rotate: -5 }}
              animate={{ rotate: 0 }}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 10,
                delay: 0.6,
              }}
            >
              {statusCode}
            </motion.div>
            <motion.p
              className="mt-2 text-gray-fg-secondary"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              {errorMessage}
            </motion.p>
          </motion.div>

          {/* Three-panel data display */}
          <motion.div
            className="flex justify-center gap-2 mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            {deploymentInfoQuery.isSuccess ? (
              <motion.button
                type="button"
                className="flex-1 px-2 py-1 text-xs text-left text-green-400 bg-graya-ui hover:bg-graya-ui-hover"
                onClick={() => copyText(deploymentInfoQuery.data.app.hash)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                ░▓ {deploymentInfoQuery.data.app.hash.substring(0, 8)}
              </motion.button>
            ) : (
              <div className="flex-1 px-2 py-1 text-xs text-left text-green-400 bg-graya-ui animate-pulse" />
            )}
            <motion.button
              type="button"
              className="flex items-center px-2 py-1 text-xs text-blue-400 bg-graya-ui hover:bg-graya-ui-hover"
              onClick={() => copyText(requestId)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              ░▓ RP#{requestId}
            </motion.button>
            {deploymentInfoQuery.isSuccess ? (
              <motion.button
                type="button"
                className="px-2 py-1 text-xs bg-graya-ui hover:bg-graya-ui-hover text-amber-400"
                onClick={() =>
                  copyText(deploymentInfoQuery.data.worker.instance)
                }
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                ░▓ {deploymentInfoQuery.data.worker.instance}
              </motion.button>
            ) : (
              <div className="flex-1 px-2 py-1 text-xs text-left bg-graya-ui text-amber-400 animate-pulse" />
            )}
          </motion.div>

          <motion.div
            className={cn(
              "mt-2 p-2 bg-graya-subtle-bg text-left",
              deploymentInfoQuery.isPending && "animate-pulse",
            )}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.4 }}
          >
            {match(deploymentInfoQuery)
              .with({ status: "success", data: P.select() }, (data) => (
                <pre className="overflow-hidden font-mono text-xs text-gray-fg-secondary">
                  {`// Deployment Info\n${JSON.stringify(data, null, 2)}`}
                </pre>
              ))
              .with({ status: "error", error: P.select() }, (error) => (
                <div className="flex items-center space-x-2">
                  <AlertTriangleIcon className="size-4 text-destructive-solid" />
                  <p className="text-sm text-gray-fg-secondary">
                    部署信息加载失败：
                    {error.message}
                  </p>
                </div>
              ))
              .with({ status: "pending" }, () => (
                <div className="flex items-center space-x-2">
                  <LoaderCircleIcon className="animate-spin size-4 text-gray-fg-secondary" />
                  <p className="text-sm text-gray-fg-secondary">
                    正在加载部署信息...
                  </p>
                </div>
              ))
              .exhaustive()}
          </motion.div>
        </div>

        <motion.div
          className="flex gap-2 mt-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.3 }}
        >
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              className="border-b-2 transition-all hover:translate-y-[1px] hover:border-b-[1px]"
              intent="soft"
              flavor="gray"
              alpha
              onClick={() => router.invalidate()}
            >
              <RotateCcwIcon />
              重新加载
            </Button>
          </motion.div>

          {isRoot ? (
            <motion.div
              className="flex-1"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                className="border-b-2 transition-all hover:translate-y-[1px] hover:border-b-[1px] w-full"
                intent="soft"
                alpha
                asChild
              >
                <Link to="/">
                  <HomeIcon />
                  返回首页
                </Link>
              </Button>
            </motion.div>
          ) : (
            <motion.div
              className="flex-1"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <Button
                className="border-b-2 transition-all hover:translate-y-[1px] hover:border-b-[1px] w-full"
                intent="soft"
                alpha
                onClick={() => window.history.back()}
              >
                <HomeIcon />
                返回上一页
              </Button>
            </motion.div>
          )}

          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Button
              className="border-b-2 transition-all hover:translate-y-[1px] hover:border-b-[1px]"
              intent="soft"
              flavor="destructive"
              alpha
              disabled={errorReported}
              onClick={() => errorReportingModal.show()}
            >
              <BugIcon />
              报告错误
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>

      <style>{`
        .animate-glitch {
          animation: glitch 2s infinite;
        }

        @keyframes glitch {
          0% { text-shadow: 0.05em 0 0 rgba(255,0,0,0.75), -0.025em -0.05em 0 rgba(0,255,0,0.75), 0.025em 0.05em 0 rgba(0,0,255,0.75); }
          14% { text-shadow: 0.05em 0 0 rgba(255,0,0,0.75), -0.025em -0.05em 0 rgba(0,255,0,0.75), 0.025em 0.05em 0 rgba(0,0,255,0.75); }
          15% { text-shadow: -0.05em -0.025em 0 rgba(255,0,0,0.75), 0.025em 0.025em 0 rgba(0,255,0,0.75), -0.05em -0.05em 0 rgba(0,0,255,0.75); }
          49% { text-shadow: -0.05em -0.025em 0 rgba(255,0,0,0.75), 0.025em 0.025em 0 rgba(0,255,0,0.75), -0.05em -0.05em 0 rgba(0,0,255,0.75); }
          50% { text-shadow: 0.025em 0.05em 0 rgba(255,0,0,0.75), 0.05em 0 0 rgba(0,255,0,0.75), 0 -0.05em 0 rgba(0,0,255,0.75); }
          99% { text-shadow: 0.025em 0.05em 0 rgba(255,0,0,0.75), 0.05em 0 0 rgba(0,255,0,0.75), 0 -0.05em 0 rgba(0,0,255,0.75); }
          100% { text-shadow: -0.025em 0 0 rgba(255,0,0,0.75), -0.025em -0.025em 0 rgba(0,255,0,0.75), -0.025em -0.05em 0 rgba(0,0,255,0.75); }
        }
      `}</style>
    </motion.div>
  );
}
